import React, { useEffect, useState, useRef } from 'react';
import { Section } from './ui/Section';
import { X } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
interface Module {
  title: string;
  description: string;
  details: {
    intro?: string;
    items: string[];
    result: string;
  };
}
export function Program() {
  const [selectedModule, setSelectedModule] = useState<number | null>(null);
  const scrollPositionRef = useRef(0);
  const openModal = (index: number) => {
    scrollPositionRef.current = window.scrollY;
    setSelectedModule(index);
  };
  const closeModal = () => {
    setSelectedModule(null);
  };
  // Lock body scroll and hide header when modal is open
  useEffect(() => {
    const header = document.querySelector('header');
    if (selectedModule !== null) {
      // Hide header
      if (header) {
        header.style.opacity = '0';
        header.style.pointerEvents = 'none';
        header.style.transition = 'opacity 0.2s ease';
      }
      // Prevent scrolling - fixed position approach
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      // Show header
      if (header) {
        header.style.opacity = '1';
        header.style.pointerEvents = 'auto';
      }
      // Restore scrolling and position
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      // Restore scroll position
      window.scrollTo(0, scrollPositionRef.current);
    }
    return () => {
      if (header) {
        header.style.opacity = '1';
        header.style.pointerEvents = 'auto';
      }
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [selectedModule]);
  const modules: Module[] = [{
    title: 'Ментальні моделі підприємця',
    description: 'Лідерство і результат — не риси характеру, а режими мислення, якими можна керувати. У кожного підприємця всередині працює набір внутрішніх програм (mindsets), сформованих досвідом, страхами, вірою та звичними реакціями.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Believer mindset (destiny +) — як внутрішня віра формує вашу реальність і рішення', 'Creator mindset — мислення автора, а не споживача чужих відповідей', 'Leader mindset (Responsibility) — відповідальність за себе, команду і систему', 'Growth mindset (Smalls steps daily X Opportunity mindset - Solutions) — ріст через маленькі кроки, а не ілюзію ідеалу', 'Opportunity mindset — бачити можливості там, де інші бачать проблеми', 'Cause & Effect mindset (Reality check) — бути причиною результатів, а не їх наслідком', 'Observer mindset — вміння вийти з емоції та подивитись на бізнес як на систему', 'Winner mindset — перемога як напрямок, а не одноразовий результат'],
      result: 'Ви починаєте приймати рішення не з реакції, а з усвідомленого вибору, що напряму впливає на маркетинг, команду і масштаб.'
    }
  }, {
    title: 'Unit-економіка для підприємця',
    description: 'Юніт-економіка – це мова, якою мислить лідер, коли вирішує: зупинятися чи масштабуватися. На цій сесії ви розберете, як маркетинг перетворюється з витрат у керований фінансовий інструмент.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Прогнозувати кількість клієнтів із рекламного бюджету', 'Рахувати CAC, вклад (Contribution), LTV, ROMI', 'Розуміти, коли перша покупка може бути збитковою, але модель залишається здоровою за рахунок LTV', 'Визначати точку беззбитковості і момент, коли масштабування стає безпечним', 'Приймати рішення на основі цифр, а не відчуттів', 'Якщо CAC ≈ середньому чеку – бізнес не масштабується', 'Якщо вклад позитивний – кожна наступна продажа працює на зростання', 'Якщо цифри не сходяться – проблема не в рекламі, а в системі'],
      result: 'Ви бачите повну фінансову логіку маркетингу і керуєте ростом, а не гадаєте, чому «не летить».'
    }
  }, {
    title: 'Formula Growth Acquisition',
    description: 'Залучення клієнтів – це не реклама. Це керована система. Formula Growth Acquisition: Aᴄ = P₁ + P₂ + P₃ + P₄ + P₅ + P₆ + P₇ + P₈. Кожен P – обовʼязковий операційний блок.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Ви розбираєте свій бізнес через 8 макрорівнів залучення: Product · Offer · Channels · Communication · Proof · Process · Team · Price', 'Чому є трафік, але немає продажів', 'Де воронка втрачає гроші', 'Що саме потрібно міняти, а що не чіпати', 'Бачите вузькі місця у системі залучення', 'Розумієте, які блоки дають зростання, а які гальмують', 'Отримуєте логіку прийняття рішень, а не список інструментів'],
      result: 'Це основа, на яку далі накладаються Product, Price, Proposition, Promotion та Process.'
    }
  }, {
    title: 'Product',
    description: 'Продукт як основа всієї системи залучення. Маркетинг не масштабує бізнес, якщо продукт не створює зрозумілу цінність для клієнта.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Цінність продукту — Що клієнт реально отримує і за що готовий платити', 'Відповідність очікуванням ЦА — Де виникає розрив між обіцянкою і реальним досвідом', 'Конкуренція та замінники — З ким продукт конкурує насправді, а не формально', 'Життєвий цикл продукту — Яка стратегія залучення доречна саме зараз', 'Масштабованість і гнучкість — Чи можна посилювати продукт без зламу моделі'],
      result: 'Чітке розуміння, що в продукті є точкою зростання. Рішення, що посилювати маркетингом, а що змінювати в продукті. База для роботи з ціною, офером і комунікацією.'
    }
  }, {
    title: 'Price',
    description: 'Ціна – це не про «дешево чи дорого». Це про те, як клієнт зчитує цінність продукту і як ціна працює всередині бізнес-моделі.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Позицію ціни на ринку — Де продукт знаходиться у ціновому коридорі відносно альтернатив', 'Логіку ціноутворення — Цінність, витрати, конкуренція', 'Сприйняття ціни клієнтом — Де виникає «дорого / дешево» і чому', 'Еластичність попиту — Як знижки та підвищення ціни реально впливають на продажі', 'Знижки та пакети — Чи підсилюють вони цінність, чи знецінюють продукт'],
      result: 'Зрозуміла і керована цінова логіка. Менше опору клієнта при покупці. Ціна працює на бізнес, а не проти нього.'
    }
  }, {
    title: 'Proposition / Offer',
    description: 'Пропозиція, яка перетворює інтерес у дію. Навіть сильний продукт не продається без чіткої пропозиції. Офер – це міст між бажанням клієнта і рішенням купити.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Фокус пропозиції — Чому клієнт має обрати саме вас', 'Формат оффера — Як подається пропозиція і як швидко зчитується', 'Обіцянку результату — Що саме отримає клієнт і в якій логіці', 'Психологічні тригери — Що реально впливає на рішення без зміни продукту', 'Переконливість і докази — Чому пропозиції можна довіряти'],
      result: 'Пропозиція, яка конвертує увагу в дію.'
    }
  }, {
    title: 'Promotion',
    description: 'Як маркетинг стабільно доносить цінність продукту. Promotion відповідає не за «креативи», а за те, чи доходить сенс продукту до потрібної аудиторії.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Канали комунікації — Де саме бізнес присутній і яку роль кожен канал відіграє', 'Маркетингові повідомлення — Чи розуміє аудиторія, що їй пропонують і навіщо це їй', 'Якість креативів — Чи здатні комунікації привертати увагу і викликати інтерес', 'Точність націлення — Відповідність повідомлень конкретним сегментам ЦА', 'Частота контактів — Баланс між недостатньою присутністю і перевантаженням аудиторії'],
      result: 'Зрозуміла логіка комунікацій. Менше втрат бюджету. Маркетинг починає працювати як система, а не набір спроб.'
    }
  }, {
    title: 'Place',
    description: 'Де і як клієнт реально купує. Place – це про доступність. Навіть за наявності попиту бізнес може його втрачати через складні точки продажу.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Точки продажу та платформи — Чи знаходить клієнт продукт там, де очікує', 'Шлях до покупки — Де клієнт губиться або відкладає рішення', 'Швидкість отримання продукту — Як час впливає на рішення купити', 'Єдність клієнтського досвіду — Наскільки узгоджені онлайн і офлайн канали'],
      result: 'Зменшення втрат на етапі покупки. Простіший і зрозуміліший шлях клієнта. Більше рішень «купити» без додаткового тиску.'
    }
  }, {
    title: 'People',
    description: 'People – це не «портрет ЦА для галочки». Цей макрорівень показує, чи бізнес говорить з правильними людьми правильним способом.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Цільова аудиторія та її логіка вибору — Хто ці клієнти, заради чого вони купують (JTBD) і як приймають рішення', 'Фінансова поведінка ЦА — Чи готові клієнти платити і за що саме', 'Команда, що взаємодіє з клієнтом — Компетенції, сервіс, довіра на фінальних етапах продажу', 'Єдність комунікації — Скрипти, підхід, тон – без хаосу всередині команди', 'Мотивація та залученість — Чи команда підтримує зростання бізнесу, а не гальмує його'],
      result: 'Чітке розуміння свого клієнта. Зрозуміла логіка роботи команди. Зростання конверсії без тиску на продаж.'
    }
  }, {
    title: 'Process',
    description: 'Process відповідає за те, чи логічний і простий шлях клієнта, і де саме бізнес втрачає людей не через продукт, а через процеси.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Шлях клієнта та воронка — Де клієнт зупиняється, відкладає або виходить', 'Кількість барʼєрів — Зайві кроки, складні дії, непотрібні форми', 'UX та зручність — Чи легко клієнту рухатись до покупки', 'Автоматизація процесів — Що можна масштабувати без ручного контролю', 'Незавершені покупки — Де і чому клієнти «зникають»'],
      result: 'Простіший клієнтський шлях. Менше втрат у воронці. Процеси, які можна передавати команді та масштабувати.'
    }
  }, {
    title: 'Physical Evidence',
    description: 'Physical Evidence показує, чи бачить клієнт підтвердження цінності продукту, і чи достатньо сигналів довіри для рішення.',
    details: {
      intro: 'Що ви опрацьовуєте',
      items: ['Візуальна упаковка і дизайн — Перше враження, яке формує очікування від продукту', 'Соціальні докази — Відгуки, кейси, рейтинги, приклади використання', 'Символи бренду — Логіка айдентики, впізнаваність, асоціації', 'Послідовність комунікації — Єдність візуалу та повідомлень у всіх точках контакту', 'Формальні підтвердження — Документи, партнери, нагороди, акредитації', 'Елементи зниження ризику — Гарантії, умови, пояснення «що буде після оплати»'],
      result: 'Менше сумнівів у клієнта. Швидше прийняття рішення. Зростання довіри без агресивного продажу.'
    }
  }, {
    title: 'Перехід до Retention',
    description: 'Rₜ – система утримання і повторного зростання. У Acquisition бізнес відповідає на питання: як клієнт прийшов і чому купив. У Retention – чому він залишився.',
    details: {
      intro: 'Ключові блоки Retention',
      items: ['Time-to-Value — Наскільки швидко клієнт отримує першу користь', 'Онбординг — Чіткий маршрут після покупки, без хаосу і втрат', 'Використання продукту — Чи стає продукт регулярною дією, а не разовою покупкою', 'Customer Success — Фокус не на відповіді, а на результат клієнта', 'Комунікації після продажу — Супровід, цінність, контакт без тиску', 'Upsell / Cross-sell — Зростання LTV без нового трафіку', 'Лояльність — Система причин залишатися, а не просто задоволення', 'Платежі та пролонгації — Контроль точок, де клієнти найчастіше губляться', 'Voice of Customer — Причини відтоку як джерело управлінських рішень', 'Аналітика і сегментація — Retention як прогнозована модель, а не випадковість'],
      result: 'Повна система утримання клієнтів та зростання LTV.'
    }
  }];
  const handleParticipateClick = () => {
    setSelectedModule(null);
    setTimeout(() => {
      const tariffsSection = document.getElementById('tariffs');
      if (tariffsSection) {
        tariffsSection.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <Section id="program" className="bg-transparent relative py-12 md:py-24" data-id="element-337">
      <div className="text-center mb-8 md:mb-16 px-3" data-id="element-338">
        <h2 className="text-xl sm:text-2xl md:text-5xl font-bold mb-2 md:mb-4" data-id="element-339">
          Програма та розклад
        </h2>
        <p className="text-xs md:text-base text-gray-400" data-id="element-340">
          12 модулів для повної трансформації бізнесу
        </p>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 px-3" data-id="element-341">
        {modules.map((module, index) => <motion.div key={index} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: index * 0.05
      }} className="h-full" data-id="element-342">
            <div className="flex flex-col h-full bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg" data-id="element-343">
              <div className="flex-1" data-id="element-344">
                <h3 className="font-bold text-base md:text-xl mb-2 md:mb-4 text-gray-900" data-id="element-345">
                  {module.title}
                </h3>
                <p className="text-gray-600 text-xs md:text-sm leading-relaxed line-clamp-3 md:line-clamp-4" data-id="element-346">
                  {module.description}
                </p>
              </div>
              <div className="mt-3 md:mt-6 pt-2 md:pt-4 border-t border-gray-200" data-id="element-347">
                <button onClick={() => openModal(index)} className="text-blue-600 font-medium text-xs md:text-sm hover:text-blue-700 transition-colors flex items-center gap-2 group" data-id="element-348">
                  Детальніше
                  <span className="group-hover:translate-x-1 transition-transform" data-id="element-349">
                    →
                  </span>
                </button>
              </div>
            </div>
          </motion.div>)}
      </div>

      <div className="text-center mt-8 md:mt-12 px-4" data-id="element-350">
        <Button href="#tariffs" size="lg" withArrow data-id="element-351">
          Прийняти участь
        </Button>
      </div>

      {/* Modal - Always on top with z-[9999] and full scroll lock */}
      <AnimatePresence data-id="element-352">
        {selectedModule !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[9999] flex items-center justify-center p-2 md:p-4 bg-black/80 backdrop-blur-sm overscroll-none" onClick={closeModal} style={{
        touchAction: 'none'
      }} onTouchMove={e => e.preventDefault()} data-id="element-353">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="relative w-full max-w-2xl max-h-[85vh] md:max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#0A0F2A] to-[#1E2A5E] rounded-xl md:rounded-2xl border border-white/10 shadow-2xl overscroll-contain" onClick={e => e.stopPropagation()} data-id="element-354">
              {/* Modal Header */}
              <div className="sticky top-0 bg-[#0A0F2A]/95 backdrop-blur-sm p-3 md:p-6 border-b border-white/10 flex items-start justify-between gap-3 z-10" data-id="element-355">
                <div className="flex-1" data-id="element-356">
                  <h3 className="font-bold text-lg md:text-2xl text-white pr-8" data-id="element-357">
                    {modules[selectedModule].title}
                  </h3>
                </div>
                <button onClick={closeModal} className="p-1.5 md:p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white absolute top-3 right-3 md:top-4 md:right-4" data-id="element-358">
                  <X className="w-5 h-5 md:w-6 md:h-6" data-id="element-359" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-3 md:p-6 space-y-3 md:space-y-6" data-id="element-360">
                <p className="text-gray-300 leading-relaxed text-xs md:text-base" data-id="element-361">
                  {modules[selectedModule].description}
                </p>

                <div className="bg-white/5 rounded-lg md:rounded-xl p-3 md:p-6 border border-white/10" data-id="element-362">
                  <h4 className="font-bold text-sm md:text-lg text-blue-400 mb-2 md:mb-4" data-id="element-363">
                    {modules[selectedModule].details.intro}
                  </h4>
                  <ul className="space-y-1.5 md:space-y-3" data-id="element-364">
                    {modules[selectedModule].details.items.map((item, i) => <li key={i} className="flex items-start gap-2 md:gap-3" data-id="element-365">
                        <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-blue-400 mt-1.5 md:mt-2 shrink-0" data-id="element-366"></div>
                        <span className="text-gray-300 text-[11px] md:text-sm leading-relaxed" data-id="element-367">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/10 rounded-lg md:rounded-xl p-3 md:p-6 border border-blue-500/20" data-id="element-368">
                  <h4 className="font-bold text-sm md:text-lg text-white mb-1 md:mb-2" data-id="element-369">
                    Результат
                  </h4>
                  <p className="text-gray-200 leading-relaxed text-xs md:text-base" data-id="element-370">
                    {modules[selectedModule].details.result}
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-[#0A0F2A]/95 backdrop-blur-sm p-3 md:p-6 border-t border-white/10 z-10" data-id="element-371">
                <Button fullWidth onClick={handleParticipateClick} data-id="element-372">
                  Прийняти участь у програмі
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </Section>;
}